import React, { useEffect } from "react";
import { __ } from "services/Translator";

// PWA-only bottom sheet for switching dashboard sections.
// Premium emerald/gold theme. Slides up from the bottom; tap the backdrop to close.
// The markup is always mounted so the open/close state can animate via the
// `is-open` class (slide + fade), instead of mount/unmount.
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M9 18l6-6-6-6"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PwaDashboardSectionSheet = ({
  open,
  onClose,
  items = [],
  activeSlug,
  onSelect,
  verifyNeedsUpload,
}) => {
  // Lock body scroll while the sheet is open; restore the previous value on close.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <div className={"pwa-section-sheet" + (open ? " is-open" : "")} role="presentation">
      <button
        type="button"
        className="pwa-section-sheet__backdrop"
        aria-label={__("Close")}
        tabIndex={open ? 0 : -1}
        onClick={onClose}
      />
      <div
        className="pwa-section-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-labelledby="pwa-section-sheet-title"
      >
        <div className="pwa-section-sheet__handle" aria-hidden="true" />
        <div className="pwa-section-sheet__header">
          <h2 id="pwa-section-sheet-title" className="pwa-section-sheet__title">
            {__("Account sections")}
          </h2>
          <button
            type="button"
            className="pwa-section-sheet__close"
            onClick={onClose}
            aria-label={__("Close")}
          >
            <CloseIcon />
          </button>
        </div>
        <nav className="pwa-section-sheet__list" aria-label={__("Account sections")}>
          {items.map((item) => {
            const isActive = activeSlug === item.slug;
            const showVerifyDot = item.slug === "verify" && verifyNeedsUpload;

            return (
              <button
                key={item.slug}
                type="button"
                className={"pwa-section-sheet__item" + (isActive ? " is-active" : "")}
                aria-current={isActive ? "page" : undefined}
                onClick={() => onSelect(item.slug)}
              >
                <span className="pwa-section-sheet__item-icon" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="pwa-section-sheet__item-label">{item.label}</span>
                {showVerifyDot && (
                  <span
                    className="pwa-section-sheet__verify-dot"
                    aria-label={__("Action required")}
                  />
                )}
                <span className="pwa-section-sheet__item-chevron" aria-hidden="true">
                  <ChevronRightIcon />
                </span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default PwaDashboardSectionSheet;
